'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { isAdmin } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ThemeToggle'
import { SystemStatus } from '@/components/SystemStatus'
import { ProjectSwitcher } from '@/components/ProjectSwitcher'
import { Avatar } from '@/components/profile/Avatar'
import { fetchProfile, initialsFor, type Profile } from '@/lib/profile'
import {
  Home,
  LayoutDashboard,
  FolderKanban,
  Wrench,
  BrainCircuit,
  LogOut,
  Menu,
  Shield,
  X,
  ChevronDown,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

const NAV = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Tools', href: '/tools', icon: Wrench },
]

// Admin-only entry. Surfaced in the same nav band when the signed-in
// user has admin/org_admin role; kept separate from NAV so the static
// array stays role-neutral.
const ADMIN_NAV = [
  // /admin redirects to /admin/invitations; the admin areas
  // (Invitations/Users/Broadcasts/Tools/Settings) live in the sub-tab
  // strip the admin layout renders. Tools holds cards for sidecar apps
  // like the database explorer (opens CloudBeaver in a new tab).
  { name: 'Admin', href: '/admin', icon: Shield },
]

/**
 * Unified industrial-lab top navigation. Used by the landing page and
 * every app shell (tools + dashboard layouts) so the chrome is
 * consistent. Wordmark → landing (/). Always visible; account controls
 * only when authenticated.
 */
export function Navigation() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const { user, logout, isAuthenticated } = useAuth()
  // The nav fetches the profile once per session to get the avatar +
  // display_name so the user sees their photo in the header and the
  // name they actually chose (instead of the auth full_name).
  const [profile, setProfile] = useState<Profile | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null)
      return
    }
    void (async () => {
      try {
        setProfile(await fetchProfile())
      } catch {
        /* fallback to plain useAuth() values */
      }
    })()
  }, [isAuthenticated])

  const isActive = (href: string) =>
    href === '/'
      ? pathname === '/'
      : pathname === href || pathname.startsWith(href + '/')

  // Append the Admin entry only when the signed-in user can use it.
  // Server-side endpoints enforce the role check independently, so a
  // user who somehow types /admin/invitations into the URL bar still
  // gets bounced (admin/layout.tsx redirects + the API returns 403).
  // Projects is a signed-in workspace surface — insert it after Dashboard
  // for authenticated users (logged-out visitors don't see a link to an
  // auth-gated page).
  const authNav = isAuthenticated
    ? [
        ...NAV.slice(0, 2),
        { name: 'Projects', href: '/projects', icon: FolderKanban },
        ...NAV.slice(2),
      ]
    : NAV
  const navItems = isAuthenticated && isAdmin(user) ? [...authNav, ...ADMIN_NAV] : authNav

  return (
    <nav className="sticky top-0 z-50 border-b border-slate-200/80 bg-slate-25/80 backdrop-blur-md">
      {/* top hairline accent */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-secondary/50 to-transparent" />
      <div className="mx-auto flex h-16 max-w-[clamp(1280px,92vw,1800px)] items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* LEFT cluster — wordmark + primary nav items (Home, Dashboard,
            Tools, Docs, About, Support). Everything in this group stays
            anchored to the left edge so the menu reads as one band. */}
        <div className="flex min-w-0 items-center gap-4">
        {/* Wordmark → landing */}
        <Link href="/" className="group flex shrink-0 items-center gap-2.5">
          <span className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-md border border-secondary/40 bg-slate-100">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/ds-emblem.png"
              alt="Designer Space"
              className="h-7 w-7 object-contain"
            />
            <span className="absolute inset-0 rounded-md shadow-glow opacity-0 transition-opacity group-hover:opacity-100" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-sm font-bold tracking-[0.14em] text-slate-900">
              OLIGOMASTER
            </span>
            <span className="data-tag mt-0.5 normal-case tracking-[0.26em] text-slate-500">
              designer&nbsp;space<sup className="text-secondary">™</sup>
            </span>
          </span>
        </Link>

        {/* Desktop menu — collapses to the hamburger below lg; labels show
            only on very wide screens (icon-only with tooltips otherwise) so
            the bar never overflows its container. */}
        <div className="hidden min-w-0 items-center gap-0.5 lg:flex">
          {navItems.map(({ name, href, icon: Icon }) => {
            const active = isActive(href)
            return (
              <Link
                key={name}
                href={href}
                title={name}
                className={`focus-ring group relative flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'text-secondary'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="hidden 2xl:inline">{name}</span>
                <span
                  className={`absolute inset-x-2 -bottom-px h-px bg-secondary transition-transform duration-200 ${
                    active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-50'
                  }`}
                />
              </Link>
            )
          })}
        </div>
        </div>

        {/* RIGHT cluster — OligoLLM CTA followed by account controls.
            OligoLLM lives here (not in NAV) so it gets the visual
            promotion of a button-shaped, secondary-cyan entry pinned to
            the right edge — the productivity surface a researcher is
            actually here for. */}
        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          {(() => {
            const oligoActive = isActive('/tools/oligollm')
            return (
              <Link
                href="/tools/oligollm"
                className={`focus-ring inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                  oligoActive
                    ? 'border-secondary bg-secondary text-white shadow-sm'
                    : 'border-secondary/30 bg-secondary/[0.06] text-secondary hover:border-secondary/60 hover:bg-secondary/[0.12]'
                }`}
                title="Open OligoLLM"
              >
                <BrainCircuit className="h-4 w-4" />
                OligoLLM
              </Link>
            )
          })()}
          {isAuthenticated && <ProjectSwitcher />}
          <div className="hidden xl:block">
            <SystemStatus enabled={isAuthenticated} />
          </div>
          <ThemeToggle />
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1 rounded-full p-0.5 outline-none transition-colors hover:bg-secondary/[0.06] data-[state=open]:bg-secondary/[0.06]"
                title="Account"
              >
                <Avatar
                  size="xs"
                  src={profile?.avatar_url}
                  initials={initialsFor({
                    display_name: profile?.display_name,
                    full_name: user.full_name,
                    email: user.email,
                  })}
                />
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                <div className="px-2 py-1.5">
                  <p className="truncate text-sm font-medium text-slate-700">
                    {profile?.display_name || user.full_name || user.email}
                  </p>
                  <p className="truncate text-xs text-slate-400">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/profile" className="w-full">Edit profile</Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => logout()}
                  className="text-red-600 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-700"
                >
                  <span className="flex items-center gap-2"><LogOut className="h-3.5 w-3.5" /> Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link href="/tools/oligollm">
              <Button size="sm" className="gap-2">
                <BrainCircuit className="h-4 w-4" /> Launch OligoLLM
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile controls */}
        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button
            onClick={() => setOpen((v) => !v)}
            className="focus-ring rounded-md p-2 text-slate-600"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-slate-200 bg-slate-25 px-4 py-3 lg:hidden">
          <div className="flex flex-col gap-1">
            {navItems.map(({ name, href, icon: Icon }) => (
              <Link
                key={name}
                href={href}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                  isActive(href)
                    ? 'bg-slate-100 text-secondary'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                {name}
              </Link>
            ))}
            <Link
              href="/tools/oligollm"
              onClick={() => setOpen(false)}
              className={`mt-1 flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${
                isActive('/tools/oligollm')
                  ? 'border-secondary bg-secondary text-white'
                  : 'border-secondary/30 bg-secondary/[0.06] text-secondary hover:bg-secondary/[0.12]'
              }`}
            >
              <BrainCircuit className="h-4 w-4" />
              OligoLLM
            </Link>
            {isAuthenticated && user && (
              <>
                <Link
                  href="/profile"
                  onClick={() => setOpen(false)}
                  className="mt-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                >
                  <Avatar
                    size="xs"
                    src={profile?.avatar_url}
                    initials={initialsFor({
                      display_name: profile?.display_name,
                      full_name: user.full_name,
                      email: user.email,
                    })}
                  />
                  {profile?.display_name || user.full_name || user.email}
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}
